import React from "react";
import { useTranslation } from "react-i18next";
import { POS_COLORS, normalizePartOfSpeech } from "@/lib/philology/relationTypes";
import MorphemeBreakdown from "./MorphemeBreakdown";
import type { DerivativeRecord } from "@/lib/philology/api";

interface DerivativeChipProps {
  derivative: DerivativeRecord;
  onClick?: (d: DerivativeRecord) => void;
  isActive?: boolean;
}

export default function DerivativeChip({ derivative, onClick, isActive }: DerivativeChipProps) {
  const { t } = useTranslation();
  const pos = normalizePartOfSpeech(derivative.partOfSpeech);
  const colors = POS_COLORS[pos];
  const lowConfidence = typeof derivative.confidence === "number" && derivative.confidence < 0.7;
  const morph = derivative.morphology;
  const hasSegments = !!(morph?.segments && morph.segments.length > 0);
  const morphLabel = morph && !hasSegments ? [morph.prefix, morph.suffix].filter(Boolean).join(" / ") : "";

  return (
    <button
      type="button"
      onClick={() => onClick?.(derivative)}
      className={`group flex flex-col items-start gap-1.5 px-3 py-2.5 rounded-xl border transition-all duration-150 text-left w-full min-h-[64px] ${colors.bg} ${colors.text} ring-1 ${colors.ring} ${
        isActive ? "ring-2 ring-offset-1 ring-purple-500 dark:ring-purple-400" : "hover:ring-2"
      } ${lowConfidence ? "opacity-70" : "opacity-100"}`}
      aria-pressed={isActive}
      title={derivative.meaning ?? undefined}
    >
      <div className="flex items-baseline gap-1.5 w-full">
        {hasSegments ? (
          <div className="flex-1 min-w-0">
            <MorphemeBreakdown word={derivative.word} morphology={morph} size="sm" />
          </div>
        ) : (
          <span className="font-semibold text-sm leading-tight truncate flex-1">{derivative.word}</span>
        )}
        {lowConfidence && (
          <span className="shrink-0 text-[10px] uppercase tracking-wide opacity-70">{t("philology.lowConfidence")}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-[11px] leading-tight w-full">
        <span className="opacity-80">{t(`philology.partsOfSpeech.${pos}`, { defaultValue: pos })}</span>
        {morphLabel && (
          <>
            <span className="opacity-40">·</span>
            <span className="font-mono opacity-70 truncate">{morphLabel}</span>
          </>
        )}
      </div>
    </button>
  );
}
